import React, { useMemo } from "react";
import { SelectProps } from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import { MomentChangeHandler } from "@core/interfaces/moment-change-handler";
import { motionSensorMode } from "@core/interfaces/motion-sensor-mode";
import { MomentErrorHandler } from "@core/interfaces/moment-error-handler";
import { lang } from "@lang/interfaces/lang";
import SelectComponent from "@ui/components/Select/Select.component";
import TimeInputComponent from "@ui/components/Time-input/Time-input.component";
import useDictionary from "@ui/hooks/useDictionary";
import SwitchComponent from "./Switch.component";
import { IDateInput } from "./useDeviceSettings";

interface IProps {
    lang: lang;
    lightSensorMode: number;
    lightSensorModes: motionSensorMode[];
    lightSensorTriggerMode: number;
    periodStart: IDateInput;
    periodEnd: IDateInput;
    isNotIgnore: boolean;
    ignoreTime: IDateInput;
    onLightSensorModeChange: SelectProps["onChange"];
    onLightSensorTriggerModeChange: SelectProps["onChange"];
    onPeriodStartChange: MomentChangeHandler;
    onPeriodEndChange: MomentChangeHandler;
    onPeriodStartError: MomentErrorHandler;
    onPeriodEndError: MomentErrorHandler;
    onNotIgnoreChange: () => void;
    onIgnoreTimeChange: MomentChangeHandler;
    onIgnoreTimeError: MomentErrorHandler;
    onInputClick: () => void;
}

const Component: React.FC<IProps> = ({
    lang,
    lightSensorMode,
    lightSensorModes,
    lightSensorTriggerMode,
    periodStart,
    periodEnd,
    isNotIgnore,
    ignoreTime,
    onLightSensorModeChange,
    onLightSensorTriggerModeChange,
    onPeriodStartChange,
    onPeriodEndChange,
    onPeriodStartError,
    onPeriodEndError,
    onNotIgnoreChange,
    onIgnoreTimeChange,
    onIgnoreTimeError,
    onInputClick,
}: IProps) => {
    const getWord = useDictionary();
    const modeList = useMemo(
        () => lightSensorModes.map((label, value) => ({ label, value })),
        [lightSensorModes]
    );

    const period = useMemo(() => {
        // Временно отключен выбор приода
        if (false /* lightSensorMode === 2 */) {
            return (
                <>
                    <TimeInputComponent
                        label="periodStart"
                        tooltip="periodStartTooltip"
                        helperText=""
                        lang={lang}
                        value={periodStart.value}
                        onChange={onPeriodStartChange}
                        onError={onPeriodStartError}
                        onInputClick={onInputClick}
                    />
                    <TimeInputComponent
                        label="periodEnd"
                        tooltip="periodEndTooltip"
                        helperText=""
                        lang={lang}
                        value={periodEnd.value}
                        onChange={onPeriodEndChange}
                        onError={onPeriodEndError}
                        onInputClick={onInputClick}
                    />
                </>
            );
        } else {
            return <></>;
        }
    }, [
        lightSensorMode,
        lang,
        periodStart.value,
        periodEnd.value,
        onPeriodStartChange,
        onPeriodStartError,
        onPeriodEndChange,
        onPeriodEndError,
        onInputClick,
    ]);

    const lightSensorModesTooltip = useMemo(
        () =>
            new Array(3)
                .fill(0)
                .map(
                    (v, i) =>
                        `${i + 1}. ${getWord(`lightSensorModesTooltip_${i}`)}.`
                ),
        [lang]
    );

    return (
        <FormControl className="device-settings__form" component="form">
            <SelectComponent
                label="operationMode"
                name="operation-mode"
                lang={lang}
                description={lightSensorModesTooltip}
                value={lightSensorMode}
                values={modeList}
                onChange={onLightSensorModeChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            {/* <SelectComponent ВРЕМЕННО УБРАНО
                label="triggeringMode"
                name="trigger-mode"
                lang={lang}
                description={"lightSensorModesTooltip"}
                value={lightSensorTriggerMode}
                values={[
                    {
                        label: "lightModeRising",
                        value: 0,
                    },
                    {
                        label: "lightModeFalling",
                        value: 1,
                    },
                ]}
                onChange={onLightSensorTriggerModeChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            /> */}
            {period}
            <TimeInputComponent
                label="sensorIgnoreTime"
                tooltip="ignoreLightTimeTooltip"
                helperText="ignoreHelperText"
                lang={lang}
                disabled={isNotIgnore || lightSensorMode === 0}
                maxTime={{ hour: 12, minute: 0 }}
                value={ignoreTime.value}
                onChange={onIgnoreTimeChange}
                onError={onIgnoreTimeError}
                onInputClick={onInputClick}
            />
            <SwitchComponent
                alarmState={isNotIgnore}
                name={"notToIgnore"}
                disabled={lightSensorMode === 0}
                tooltip={getWord("notToIgnoreLightTooltip")}
                onAlarmStateChange={onNotIgnoreChange}
            />
        </FormControl>
    );
};

const LightSensorSettingsComponent = React.memo(Component);

export default LightSensorSettingsComponent;
